// src/renderer/hooks/useProjectOpen.ts

import { useState, useCallback, useRef, useEffect } from 'react';
import { useProjectStore } from '../store/projectStore';
import { useAppStore } from '../store';
import {
  getProjectHandle,
  verifyPermission,
  requestPermission,
} from '../utils/handle-storage';
import type { ProjectErrorType } from '../components/dialogs/ProjectErrorDialog';
import type { ProjectManifest, RecentProject } from '../../core/types/project.types';
import { refreshProjectFiles } from '../utils/project-files';
import {
  showDirectoryPicker,
  validateProject,
  loadProjectManifest,
  setProjectRoot,
  addToRecentProjects,
  removeFromRecentProjects,
} from '../services/FileSystemService';
import { ProjectStateManager } from '../services/ProjectStateManager';

// File watcher configuration
const FILE_WATCHER_POLL_INTERVAL = 5000; // 5 seconds

export interface ProjectOpenError {
  type: ProjectErrorType;
  details?: string;
  path?: string;
}

interface UseProjectOpenReturn {
  isOpening: boolean;
  error: ProjectOpenError | null;
  openProject: () => Promise<boolean>;
  openFromRecent: (projectPath: string) => Promise<boolean>;
  clearError: () => void;
  retryOpen: () => Promise<boolean>;
  stopFileWatcher: () => void;
}

// Current engine version for compatibility checks
const CURRENT_ENGINE_VERSION = '8.0.0';

/**
 * Hook for opening existing projects with full validation and error handling.
 * Supports both new project selection and opening from recent projects list.
 */
export function useProjectOpen(): UseProjectOpenReturn {
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<ProjectOpenError | null>(null);
  const [lastAttemptedPath, setLastAttemptedPath] = useState<string | null>(null);
  const [lastAttemptedHandle, setLastAttemptedHandle] = useState<FileSystemDirectoryHandle | null>(null);

  // File watcher state
  const fileWatcherIntervalRef = useRef<number | null>(null);

  const { setCurrentProject, addRecentProject, removeRecentProject } = useProjectStore();
  const { setCurrentView } = useAppStore();

  /**
   * Start the file watcher to periodically refresh project files.
   * This detects external changes to the file system.
   */
  const startFileWatcher = useCallback(() => {
    // Don't start if already running
    if (fileWatcherIntervalRef.current) return;

    const checkForChanges = async () => {
      try {
        await refreshProjectFiles();
      } catch (err) {
        console.warn('[FileWatcher] Failed to refresh:', err);
      }
    };

    fileWatcherIntervalRef.current = window.setInterval(checkForChanges, FILE_WATCHER_POLL_INTERVAL);
    console.log('[FileWatcher] Started watching with interval:', FILE_WATCHER_POLL_INTERVAL);
  }, []);

  /**
   * Stop the file watcher.
   */
  const stopFileWatcher = useCallback(() => {
    if (fileWatcherIntervalRef.current) {
      clearInterval(fileWatcherIntervalRef.current);
      fileWatcherIntervalRef.current = null;
      console.log('[FileWatcher] Stopped watching');
    }
  }, []);

  // Cleanup file watcher on unmount
  useEffect(() => {
    return () => {
      if (fileWatcherIntervalRef.current) {
        clearInterval(fileWatcherIntervalRef.current);
      }
    };
  }, []);

  /**
   * Validate project and open it if valid.
   * Sets the project root, loads manifest, saves handle, and navigates to editor.
   */
  const validateAndOpenProject = useCallback(async (
    handle: FileSystemDirectoryHandle,
    path: string
  ): Promise<boolean> => {
    // Validate project structure by checking for .project.json
    const validation = await validateProject(handle);

    if (!validation.valid || !validation.manifest) {
      setError({
        type: 'not-valid-project',
        details: validation.error || 'The selected folder does not contain a valid .project.json file.',
        path,
      });
      return false;
    }

    const manifest = validation.manifest;

    // Check version compatibility
    if (manifest.engineVersion) {
      const comparison = compareVersions(manifest.engineVersion, CURRENT_ENGINE_VERSION);
      if (comparison > 0) {
        setError({
          type: 'version-incompatible',
          details: `Project requires engine version ${manifest.engineVersion}, but current version is ${CURRENT_ENGINE_VERSION}`,
          path,
        });
        return false;
      }
    }

    // Set as active project root
    setProjectRoot(handle);

    // Initialize ProjectStateManager for this project
    ProjectStateManager.initialize(path);

    // Load saved project state from .editor-state/ folder
    try {
      const stateBundle = await ProjectStateManager.loadProjectState();
      await ProjectStateManager.initializeStores(stateBundle);
      console.log('[useProjectOpen] Project state loaded successfully');
    } catch (err) {
      console.warn('[useProjectOpen] Failed to load project state, using defaults:', err);
      // Non-critical error - continue with default state
    }

    // Set up dirty tracking to detect changes after state is loaded
    try {
      const { useProjectStore } = await import('../store/projectStore');
      const { setupDirtyTracking } = useProjectStore.getState();
      await setupDirtyTracking();
      console.log('[useProjectOpen] Dirty tracking initialized');
    } catch (err) {
      console.warn('[useProjectOpen] Failed to setup dirty tracking:', err);
    }

    // Refresh project files from the real file system
    try {
      await refreshProjectFiles();
    } catch (err) {
      console.warn('Failed to refresh project files:', err);
      // Non-critical error, continue with project open
    }

    // Load manifest fully (this also updates timestamps)
    let loadedManifest: ProjectManifest;
    try {
      loadedManifest = await loadProjectManifest();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError({
        type: 'corrupted-manifest',
        details: message,
        path,
      });
      return false;
    }

    // Create recent project entry
    const recentProject: RecentProject = {
      name: loadedManifest.name,
      path,
      size: 0, // Size calculation is expensive, skip for now
      lastOpened: new Date().toISOString(),
    };

    // Add to recent projects (saves handle to IndexedDB and updates localStorage)
    try {
      await addToRecentProjects(recentProject, handle);
    } catch {
      // Non-critical error, continue
      console.warn('Failed to add project to recent list');
    }

    // Update store
    addRecentProject(recentProject);
    setCurrentProject(loadedManifest, path);

    // Navigate to editor
    setCurrentView('editor');

    // If no scene is loaded yet, load the start scene from manifest
    try {
      const { useSceneStore } = await import('../store/sceneStore');
      const { currentScenePath } = useSceneStore.getState();
      if (!currentScenePath && loadedManifest.startScene) {
        await useSceneStore.getState().loadScene(loadedManifest.startScene);
        console.log('[useProjectOpen] Start scene loaded:', loadedManifest.startScene);
      }
    } catch (err) {
      console.warn('[useProjectOpen] Failed to load start scene:', err);
    }

    // Start file watcher to detect external changes (non-blocking)
    startFileWatcher();

    return true;
  }, [setCurrentProject, addRecentProject, setCurrentView, startFileWatcher]);

  /**
   * Open a project by showing directory picker dialog.
   * Returns true if project was opened successfully, false otherwise.
   */
  const openProject = useCallback(async (): Promise<boolean> => {
    setIsOpening(true);
    setError(null);

    try {
      const handle = await showDirectoryPicker();

      if (!handle) {
        // User cancelled
        setIsOpening(false);
        return false;
      }

      const path = handle.name;

      // Store for potential retry
      setLastAttemptedHandle(handle);
      setLastAttemptedPath(path);

      return await validateAndOpenProject(handle, path);
    } catch (err: unknown) {
      console.error('Failed to open project:', err);

      // User canceled - not an error
      if (err instanceof Error && err.name === 'AbortError') {
        return false;
      }

      const message = err instanceof Error ? err.message : 'Unknown error';
      setError({
        type: 'unknown',
        details: message,
      });
      return false;
    } finally {
      setIsOpening(false);
    }
  }, [validateAndOpenProject]);

  /**
   * Open a project from the recent projects list using stored handle.
   * Handles permission verification and re-requests if needed.
   */
  const openFromRecent = useCallback(async (projectPath: string): Promise<boolean> => {
    setIsOpening(true);
    setError(null);
    setLastAttemptedPath(projectPath);

    try {
      // Get stored handle from IndexedDB
      const handle = await getProjectHandle(projectPath);

      if (!handle) {
        // Handle not found - project was likely removed or browser data cleared
        setError({
          type: 'folder-not-found',
          path: projectPath,
        });
        // Remove from recent since handle is gone
        removeRecentProject(projectPath);
        await removeFromRecentProjects(projectPath);
        return false;
      }

      setLastAttemptedHandle(handle);

      // Check if we have permission
      const hasPermission = await verifyPermission(handle);

      if (!hasPermission) {
        // Request permission from user
        const granted = await requestPermission(handle);

        if (!granted) {
          setError({
            type: 'permission-denied',
            path: projectPath,
          });
          return false;
        }
      }

      return await validateAndOpenProject(handle, projectPath);
    } catch (err: unknown) {
      console.error('Failed to open recent project:', err);

      if (err instanceof Error) {
        if (err.name === 'NotFoundError') {
          setError({
            type: 'folder-not-found',
            path: projectPath,
          });
          removeRecentProject(projectPath);
          await removeFromRecentProjects(projectPath);
        } else if (err.name === 'NotAllowedError') {
          setError({
            type: 'permission-denied',
            path: projectPath,
          });
        } else {
          setError({
            type: 'unknown',
            details: err.message,
            path: projectPath,
          });
        }
      } else {
        setError({
          type: 'unknown',
          details: 'An unknown error occurred',
          path: projectPath,
        });
      }

      return false;
    } finally {
      setIsOpening(false);
    }
  }, [validateAndOpenProject, removeRecentProject]);

  /**
   * Clear the current error state.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Retry the last attempted open operation.
   * Re-requests permission if that was the issue.
   */
  const retryOpen = useCallback(async (): Promise<boolean> => {
    if (lastAttemptedHandle && lastAttemptedPath) {
      setIsOpening(true);
      setError(null);

      try {
        // Request permission again
        const granted = await requestPermission(lastAttemptedHandle);

        if (!granted) {
          setError({
            type: 'permission-denied',
            path: lastAttemptedPath,
          });
          return false;
        }

        return await validateAndOpenProject(lastAttemptedHandle, lastAttemptedPath);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError({
          type: 'unknown',
          details: message,
          path: lastAttemptedPath,
        });
        return false;
      } finally {
        setIsOpening(false);
      }
    }

    // No previous attempt to retry - start fresh
    return openProject();
  }, [lastAttemptedHandle, lastAttemptedPath, validateAndOpenProject, openProject]);

  return {
    isOpening,
    error,
    openProject,
    openFromRecent,
    clearError,
    retryOpen,
    stopFileWatcher,
  };
}

/**
 * Compare two semantic version strings.
 * Returns: positive if a > b, negative if a < b, 0 if equal
 */
function compareVersions(a: string, b: string): number {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const partA = partsA[i] || 0;
    const partB = partsB[i] || 0;

    if (partA > partB) return 1;
    if (partA < partB) return -1;
  }

  return 0;
}

export default useProjectOpen;
